import React from 'react';
import PropTypes from 'prop-types';
import { Table } from 'antd';
import { captureAnalytics } from '../../../actions/preload';
import COLORS from '../../../util/constants/colors';
import { ProgressRow } from './SummaryCards';
import { formatCount, formatRangeLabel } from './helpers';

const initials = (name) => (name || '?')
  .split(/\s+/)
  .map((part) => part[0])
  .join('')
  .slice(0, 2)
  .toUpperCase();

const countColumn = (key, title, className) => ({
  key,
  title,
  dataIndex: key,
  align: 'right',
  className,
  sorter: (a, b) => a[key] - b[key],
  render: formatCount,
});

const COLUMNS = [
  {
    key: 'name',
    title: 'Team member',
    dataIndex: 'name',
    sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    render: (name, member) => (
      <div className="user-activity-member">
        <span className="user-activity-avatar">{initials(name)}</span>
        <div>
          <strong>{name}</strong>
          <div className="user-activity-caption">{member.departments.join(' · ')}</div>
        </div>
      </div>
    ),
  },
  countColumn('charts_created', 'Charts created'),
  countColumn('tasks_created', 'Tasks created'),
  countColumn('sent_to_chart', 'Sent to chart'),
  countColumn('merged', 'Merged'),
  countColumn('status_changes', 'Status changes'),
  // Current workload, not selected-date activity: shaded apart from the rest.
  countColumn('assigned_now', 'Assigned records · now', 'user-activity-now-column'),
];

const MovedTo = ({ movedTo }) => {
  const max = Math.max(...movedTo.map((entry) => entry.count));

  return (
    <div className="user-activity-moved-to">
      <strong>Moved to status</strong>
      <span className="user-activity-caption">Changes by this person · selected dates</span>
      <div className="user-activity-progress-list">
        {movedTo.map((entry) => (
          <ProgressRow
            key={entry.status}
            label={entry.status}
            count={entry.count}
            max={max}
            color={COLORS.PURPLE_SECONDARY_LITE}
          />
        ))}
      </div>
    </div>
  );
};

MovedTo.propTypes = {
  movedTo: PropTypes.arrayOf(PropTypes.shape({
    status: PropTypes.string,
    count: PropTypes.number,
  })).isRequired,
};

const renderMovedTo = (member) => <MovedTo movedTo={member.moved_to} />;

const TeamActivity = ({ team }) => (
  <div className="user-activity-card user-activity-team">
    <div className="user-activity-section-header">
      <div>
        <h3>Team activity</h3>
        <span className="user-activity-caption">
          Work completed in selected dates · Assigned records show current workload, across all record types
        </span>
      </div>
      <span className="user-activity-caption">
        {formatRangeLabel({ startDate: team.date_range.start_date, endDate: team.date_range.end_date })}
      </span>
    </div>
    <Table
      rowKey="user_id"
      columns={COLUMNS}
      dataSource={team.members}
      pagination={false}
      size="middle"
      expandable={{
        rowExpandable: (member) => member.moved_to.length > 0,
        expandedRowRender: renderMovedTo,
        onExpand: (expanded) => { if (expanded) captureAnalytics('user_activity_team_member_expanded'); },
      }}
    />
  </div>
);

TeamActivity.propTypes = {
  team: PropTypes.shape({
    date_range: PropTypes.object.isRequired,
    members: PropTypes.array.isRequired,
  }).isRequired,
};

export default TeamActivity;
